import { toast } from '@econsult/econsult-ui/dist/components/Toast';
import { ErrorToast, SuccessToast } from 'components/Toast';
import { STRINGS } from 'config';
import { SnomedEntry } from 'model/toolbar/snomed';
import React, { Dispatch } from 'react';
import { updateAiSnomedCodeCounts } from '../snomed';
/* eslint-disable @typescript-eslint/no-explicit-any */

function separateSnomedCodes(
  aiSnomedCodes: { code: string }[],
  selectedSnomed: string[],
): { enabled: string[]; ignored: string[] } {
  const enabled: string[] = [];
  const ignored: string[] = [];
  aiSnomedCodes.forEach(({ code }) => {
    if (selectedSnomed.includes(code)) {
      enabled.push(code);
    } else {
      ignored.push(code);
    }
  });
  return { enabled, ignored };
}

const {
  REDUCERS: {
    TOOLBAR: { WRITE_TO_RECORD_RESPONSE, WRITE_TO_RECORD_REQUEST, WRITE_TO_RECORD_FAILED },
  },
} = STRINGS;

interface WriteToRecordPayload {
  patientId: string;
  history: string | null;
  examination: string | null;
  procedure: string | null;
  allergy: string | null;
  familyHistory: string | null;
  summary: string | null;
  snomedCodes: SnomedEntry[];
}

export const writeToRecord = async (dispatch: Dispatch<unknown>, payload: WriteToRecordPayload): Promise<void> => {
  dispatch({ type: WRITE_TO_RECORD_REQUEST });
  try {
    await fetch(`${window.location.protocol}//toolbar/summariserWriteToRecord`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    // This call will always fail by design.
  }
};

/*
    Toolbar status meaning
    500 - a general error
    401 - the user isn't logged into the clinical system
    403 - the user doesn't have the right access rights in the clinical system
    406 - the user has a patient record open in system one
*/
interface SummariserWriteToRecordResponse {
  status: number;
}

export const injectSummariserWriteToRecordResponse =
  (dispatch: React.Dispatch<unknown>, getState: any) =>
  (response: SummariserWriteToRecordResponse): unknown => {
    const {
      snomed: { aiSnomedCodes, selectedSnomed },
      app: { selectedPractice },
    } = getState();
    const selectedPracticeId = selectedPractice?.id;
    const timestamp = new Date().getTime();
    if (response.status === 200) {
      if (aiSnomedCodes?.length) {
        const { enabled, ignored } = separateSnomedCodes(aiSnomedCodes, selectedSnomed);
        if (selectedPracticeId) {
          updateAiSnomedCodeCounts(dispatch, { enabled, ignored }, selectedPracticeId);
        }
      }
      dispatch({
        type: WRITE_TO_RECORD_RESPONSE,
        payload: { ...response, timestamp },
      });
      toast.success(React.createElement(SuccessToast, { message: 'Successfully written to record' }), {
        icon: false,
      });
      return null;
    }
    dispatch({
      type: WRITE_TO_RECORD_FAILED,
      payload: response,
    });

    const errorMessage = {
      401: 'You are not logged in to the clinical system.',
      403: 'You do not have the correct permissions in the clinical system.',
      404: 'No patient record is open in the clinical system.',
      500: 'Failed to send summary.',
    };

    return toast.error(
      React.createElement(ErrorToast, {
        message: errorMessage[response.status as keyof typeof errorMessage] ?? 'Failed to send summary',
      }),
      {
        autoClose: 12000,
        pauseOnHover: true,
        hideProgressBar: false,
        closeOnClick: true,
        icon: false,
      },
    );
  };
